/**
 * Admin Module Pages - Extracted from App.js
 * Contains: WhatsAppPage, ReportsPage, LeaveManagementPage, LeaveApplyPage, EmergencyExitPage, UDISEPage
 */
import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Toast } from '../components/shared';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// API Helper
const api = {
  get: async (endpoint, token) => {
    try {
      const res = await fetch(`${API_URL}${endpoint}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      return await res.json();
    } catch (err) {
      return { error: err.message };
    }
  },
  post: async (endpoint, data, token) => {
    try {
      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(data)
      });
      return await res.json();
    } catch (err) {
      return { error: err.message };
    }
  }
};

// ==================== TEMPLATE MANAGER COMPONENT ====================
function TemplateManager({ token, templates, loadTemplates, toast, setToast }) {
  const [presets, setPresets] = useState([]);
  const [submitting, setSubmitting] = useState(null);
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [customTemplate, setCustomTemplate] = useState({
    name: '', category: 'UTILITY', body_text: '', footer_text: 'System Of Success',
    buttons: [], body_example: []
  });
  const [subTab, setSubTab] = useState('existing');

  useEffect(() => { loadPresets(); }, []);

  const loadPresets = async () => {
    try {
      const res = await api.get('/api/whatsapp/templates/presets', token);
      if (res.success) setPresets(res.presets);
    } catch (err) { console.error(err); }
  };

  const submitPreset = async (presetId) => {
    setSubmitting(presetId);
    try {
      const res = await api.post(`/api/whatsapp/templates/submit-preset/${presetId}`, {}, token);
      if (res.success) {
        setToast({ message: `Template "${res.name}" submitted! Status: ${res.status}`, type: 'success' });
        loadTemplates();
      } else {
        setToast({ message: res.error || 'Submit failed', type: 'error' });
      }
    } catch (err) {
      setToast({ message: 'Failed to submit', type: 'error' });
    }
    setSubmitting(null);
  };

  const submitCustom = async () => {
    if (!customTemplate.name || !customTemplate.body_text) {
      setToast({ message: 'Name aur Body text required hai', type: 'error' });
      return;
    }
    setSubmitting('custom');
    try {
      const payload = {
        ...customTemplate,
        name: customTemplate.name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, ''),
        body_example: customTemplate.body_text.match(/\{\{\d+\}\}/g)
          ? customTemplate.body_example.filter(v => v) : undefined
      };
      const res = await api.post('/api/whatsapp/templates/create', payload, token);
      if (res.success) {
        setToast({ message: `Template "${res.name}" submitted! Status: ${res.status}`, type: 'success' });
        setShowCustomForm(false);
        setCustomTemplate({ name: '', category: 'UTILITY', body_text: '', footer_text: 'System Of Success', buttons: [], body_example: [] });
        loadTemplates();
      } else {
        setToast({ message: res.error || res.details || 'Submit failed', type: 'error' });
      }
    } catch (err) {
      setToast({ message: 'Failed to submit', type: 'error' });
    }
    setSubmitting(null);
  };

  const syncTemplates = async () => {
    setSubmitting('sync');
    const res = await api.post('/api/whatsapp/templates/sync-all', {}, token);
    if (res.success) {
      setToast({ message: `Synced: ${res.approved} approved, ${res.pending} pending, ${res.rejected} rejected`, type: 'success' });
      loadTemplates();
    }
    setSubmitting(null);
  };

  const varCount = (text) => (text.match(/\{\{\d+\}\}/g) || []).length;

  return (
    <div className="space-y-4" data-testid="template-manager">
      {/* Sub-tabs */}
      <div className="flex gap-2">
        {[
          { id: 'existing', label: `Existing (${templates.length})` },
          { id: 'presets', label: `Quick Presets (${presets.length})` },
          { id: 'custom', label: 'Create Custom' }
        ].map(t => (
          <button key={t.id} onClick={() => setSubTab(t.id)} data-testid={`tmpl-tab-${t.id}`}
            className={`px-4 py-2 rounded-lg text-sm ${subTab === t.id ? 'bg-green-500 text-white' : 'bg-gray-100 hover:bg-gray-200'}`}>
            {t.label}
          </button>
        ))}
        <button onClick={syncTemplates} disabled={submitting === 'sync'} className="ml-auto px-3 py-2 text-sm text-green-600 border border-green-200 rounded-lg hover:bg-green-50 disabled:opacity-50" data-testid="sync-templates-btn">
          {submitting === 'sync' ? 'Syncing...' : 'Sync from Meta'}
        </button>
      </div>

      {/* Existing Templates */}
      {subTab === 'existing' && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="hidden grid-cols-[2fr_1fr_1fr_1fr] gap-4 bg-gray-50 px-4 py-3 text-left text-xs font-medium uppercase text-gray-500 md:grid" data-testid="existing-templates-header">
            <div>Name</div>
            <div>Category</div>
            <div>Language</div>
            <div>Status</div>
          </div>
          <div className="divide-y" data-testid="existing-templates-list">
            {templates.map((t, i) => (
              <div key={i} className="grid gap-3 px-4 py-3 hover:bg-gray-50 md:grid-cols-[2fr_1fr_1fr_1fr] md:items-center" data-testid={`existing-template-${i}`}>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gray-400 md:hidden">Name</p>
                  <p className="font-medium text-gray-800">{t.name}</p>
                </div>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gray-400 md:hidden">Category</p>
                  <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${t.category === 'AUTHENTICATION' ? 'bg-purple-100 text-purple-700' : t.category === 'MARKETING' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'}`}>{t.category}</span>
                </div>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gray-400 md:hidden">Language</p>
                  <p className="text-sm text-gray-600">{t.language}</p>
                </div>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gray-400 md:hidden">Status</p>
                  <span className={`inline-block px-2 py-1 rounded text-xs ${t.status === 'APPROVED' ? 'bg-green-100 text-green-700' : t.status === 'REJECTED' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>{t.status}</span>
                </div>
              </div>
            ))}
            {templates.length === 0 && <div className="px-4 py-8 text-center text-gray-500">No templates</div>}
          </div>
        </div>
      )}

      {/* Quick Presets - One Click Submit */}
      {subTab === 'presets' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {presets.map(p => {
            const alreadyExists = templates.some(t => t.name === p.name);
            return (
              <div key={p.id} className="bg-white rounded-xl shadow-sm border p-4 space-y-3">
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-bold text-gray-800">{p.name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</h4>
                    <span className={`text-xs px-2 py-0.5 rounded ${p.category === 'MARKETING' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'}`}>{p.category}</span>
                  </div>
                  <span className="text-xs text-gray-400">{p.variables_count} variables{p.has_buttons ? ' + Buttons' : ''}</span>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600 whitespace-pre-line max-h-32 overflow-y-auto">{p.body_text}</div>
                {p.example_values.length > 0 && (
                  <p className="text-xs text-gray-400">Example: {p.example_values.join(', ')}</p>
                )}
                <button
                  onClick={() => submitPreset(p.id)}
                  disabled={alreadyExists || submitting === p.id}
                  className={`w-full py-2 rounded-lg text-sm font-medium transition ${alreadyExists ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-green-500 text-white hover:bg-green-600 disabled:opacity-50'}`}
                  data-testid={`submit-preset-${p.id}`}
                >
                  {submitting === p.id ? 'Submitting...' : alreadyExists ? 'Already Exists' : 'Submit for Approval'}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Custom Template Creator */}
      {subTab === 'custom' && (
        <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
          <h3 className="font-bold text-gray-700">Create Custom Template</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Template Name</label>
              <input type="text" value={customTemplate.name} onChange={e => setCustomTemplate({...customTemplate, name: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="my_template_name (lowercase, underscores)" data-testid="custom-tmpl-name" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Category</label>
              <select value={customTemplate.category} onChange={e => setCustomTemplate({...customTemplate, category: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg text-sm">
                <option value="UTILITY">Utility (Transactional)</option>
                <option value="MARKETING">Marketing (Promotional)</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">Body Text <span className="text-gray-400">(Use {"{{1}}"}, {"{{2}}"} for variables)</span></label>
            <textarea value={customTemplate.body_text} onChange={e => setCustomTemplate({...customTemplate, body_text: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg text-sm" rows={4} placeholder="Namaste {{1}}, your payment of ₹{{2}} is confirmed." data-testid="custom-tmpl-body" />
            {varCount(customTemplate.body_text) > 0 && (
              <div className="mt-2 space-y-1">
                <p className="text-xs text-gray-500">{varCount(customTemplate.body_text)} variable(s) detected — provide example values:</p>
                {Array.from({ length: varCount(customTemplate.body_text) }, (_, i) => (
                  <input key={i} type="text" placeholder={`Example for {{${i+1}}}`}
                    value={customTemplate.body_example[i] || ''}
                    onChange={e => {
                      const ex = [...customTemplate.body_example];
                      ex[i] = e.target.value;
                      setCustomTemplate({...customTemplate, body_example: ex});
                    }}
                    className="w-full px-3 py-1.5 border rounded text-xs" />
                ))}
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">Footer Text (optional)</label>
            <input type="text" value={customTemplate.footer_text} onChange={e => setCustomTemplate({...customTemplate, footer_text: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="System Of Success" />
          </div>
          <button onClick={submitCustom} disabled={submitting === 'custom' || !customTemplate.name || !customTemplate.body_text}
            className="w-full py-3 bg-green-500 text-white rounded-xl font-bold hover:bg-green-600 disabled:opacity-50 transition" data-testid="submit-custom-tmpl">
            {submitting === 'custom' ? 'Submitting to Meta...' : 'Submit for Meta Approval'}
          </button>
          <p className="text-xs text-gray-400 text-center">Template Meta review में 24 hours तक लग सकते हैं। Approve होने पर automatically use हो जाएगा।</p>
        </div>
      )}
    </div>
  );
}


// ==================== WHATSAPP PAGE ====================
export function WhatsAppPage({ token, user }) {
  const { language } = useLanguage();
  const isSchoolAdmin = user?.role === 'school_admin';
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('control');
  const [toast, setToast] = useState(null);
  const [logs, setLogs] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [webhookStats, setWebhookStats] = useState(null);
  const [inbox, setInbox] = useState([]);
  const [inboxUnread, setInboxUnread] = useState(0);
  const [inboxSearch, setInboxSearch] = useState('');
  const [inboxLoading, setInboxLoading] = useState(false);
  const [sendForm, setSendForm] = useState({ phone: '', template_name: '', language: 'en_US', parameters: '' });
  const [sending, setSending] = useState(false);
  const [schoolHealth, setSchoolHealth] = useState([]);
  const [healthSummary, setHealthSummary] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [healthSearch, setHealthSearch] = useState('');
  const [healthStatusFilter, setHealthStatusFilter] = useState('all');
  const [healthPagination, setHealthPagination] = useState({ page: 1, total_pages: 0, total_items: 0 });
  const [selectedHealthSchool, setSelectedHealthSchool] = useState(null);

  useEffect(() => {
    if (isSchoolAdmin) {
      setLoading(false);
      return;
    }

    const init = async () => {
      await Promise.all([checkStatus(), loadLogs(), loadTemplates(), loadWebhookStats(), loadSchoolHealth(), loadInbox()]);
      setLoading(false);
    };
    init();
  }, [isSchoolAdmin]);

  // Live polling for the Inbox tab (every 12s) so verification codes appear quickly.
  useEffect(() => {
    if (isSchoolAdmin || activeTab !== 'inbox') return;
    const t = setInterval(() => loadInbox(), 12000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, isSchoolAdmin, inboxSearch]);

  const checkStatus = async () => {
    try {
      const res = await api.get('/api/whatsapp/status', token);
      setStatus(res);
    } catch (err) {
      console.error('Status check failed:', err);
    }
  };
  
  const loadLogs = async () => {
    try {
      const res = await api.get('/api/whatsapp/logs?limit=50', token);
      setLogs(res.logs || []);
    } catch (err) {
      console.error('Logs load failed:', err);
    }
  };

  const loadTemplates = async () => {
    try {
      const res = await api.get('/api/whatsapp/templates', token);
      setTemplates(res.templates || []);
    } catch (err) {
      console.error('Templates load failed:', err);
    }
  };

  const loadWebhookStats = async () => {
    try {
      const res = await api.get('/api/whatsapp/webhook-stats', token);
      if (res.success) setWebhookStats(res.stats);
    } catch (err) {
      console.error('Webhook stats failed:', err);
    }
  };

  const loadInbox = async (q = inboxSearch) => {
    setInboxLoading(true);
    try {
      const res = await api.get(`/api/whatsapp/inbox?limit=150&q=${encodeURIComponent(q || '')}`, token);
      if (res.success) {
        setInbox(res.messages || []);
        setInboxUnread(res.unread || 0);
      }
    } catch (err) {
      console.error('Inbox load failed:', err);
    } finally {
      setInboxLoading(false);
    }
  };

  const markInboxRead = async () => {
    try {
      await api.post('/api/whatsapp/inbox/mark-read', {}, token);
      setInboxUnread(0);
      loadInbox();
    } catch (err) {
      console.error('Inbox mark-read failed:', err);
    }
  };

  const loadSchoolHealth = async (overrides = {}) => {
    setHealthLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(overrides.page || 1),
        limit: '12',
      });

      const activeSearch = overrides.search ?? healthSearch;
      const activeStatus = overrides.status ?? healthStatusFilter;
      const targetSchoolId = overrides.schoolId;

      if (activeSearch) params.set('search', activeSearch);
      if (activeStatus && activeStatus !== 'all') params.set('status', activeStatus);
      if (targetSchoolId) params.set('school_id', targetSchoolId);

      const res = await api.get(`/api/whatsapp/platform/school-health?${params.toString()}`, token);
      if (res.success) {
        if (targetSchoolId && res.items?.length) {
          setSelectedHealthSchool(res.items[0]);
          setSchoolHealth((prev) => prev.map((item) => item.school_id === targetSchoolId ? res.items[0] : item));
        } else {
          setSchoolHealth(res.items || []);
          setHealthSummary(res.summary || null);
          setHealthPagination(res.pagination || { page: 1, total_pages: 0, total_items: 0 });
        }
      }
    } catch (err) {
      console.error('School health load failed:', err);
    }
    setHealthLoading(false);
  };

  const platformSummaryCards = [
    {
      id: 'api-status',
      title: language === 'hi' ? 'API Status' : 'API Status',
      value: status?.configured ? (language === 'hi' ? 'Connected' : 'Connected') : (language === 'hi' ? 'Pending' : 'Pending'),
      note: status?.phone_info?.display_phone_number || (language === 'hi' ? 'Phone ID और token verify करें' : 'Verify phone ID and token'),
      color: status?.configured ? 'from-green-500 to-emerald-600' : 'from-slate-500 to-slate-600',
    },
    {
      id: 'templates',
      title: language === 'hi' ? 'Templates' : 'Templates',
      value: `${templates.length}`,
      note: language === 'hi' ? 'Approved / synced templates' : 'Approved / synced templates',
      color: 'from-blue-500 to-indigo-600',
    },
    {
      id: 'delivery',
      title: language === 'hi' ? 'Delivery Rate' : 'Delivery Rate',
      value: `${webhookStats?.delivery_rate || 0}%`,
      note: language === 'hi' ? 'Webhook delivery + read tracking' : 'Webhook delivery + read tracking',
      color: 'from-orange-500 to-amber-600',
    },
    {
      id: 'ownership',
      title: language === 'hi' ? 'School Ownership Rule' : 'School Ownership Rule',
      value: language === 'hi' ? 'School own WA' : 'School own WA',
      note: language === 'hi' ? 'School Admin अपना खुद का WhatsApp Social Media में connect करेगा' : 'School Admin connects its own WhatsApp inside Social Media',
      color: 'from-fuchsia-500 to-pink-600',
    },
  ];

  const healthBadgeClass = (status) => {
    if (status === 'connected') return 'bg-green-100 text-green-700';
    if (status === 'token_issue') return 'bg-red-100 text-red-700';
    return 'bg-gray-100 text-gray-600';
  };

  if (isSchoolAdmin) {
    const quickLinks = [
      { href: '/communication', title: language === 'hi' ? 'Announcements' : 'Announcements', note: language === 'hi' ? 'स्कूल के notices और circulars भेजें' : 'Send school notices and circulars', testId: 'school-admin-announcements-link' },
      { href: '/complaints', title: language === 'hi' ? 'Complaints' : 'Complaints', note: language === 'hi' ? 'शिकायतें ट्रैक और resolve करें' : 'Track and resolve complaints', testId: 'school-admin-complaints-link' },
      { href: '/notifications', title: language === 'hi' ? 'Notifications' : 'Notifications', note: language === 'hi' ? 'स्कूल alerts और reminders manage करें' : 'Manage school alerts and reminders', testId: 'school-admin-notifications-link' },
      { href: '/social-media', title: language === 'hi' ? 'Social Media' : 'Social Media', note: language === 'hi' ? 'Platforms → WhatsApp Business से अपना खुद का WhatsApp connect करें' : 'Connect your own WhatsApp from Platforms → WhatsApp Business', testId: 'school-admin-social-media-link' },
      { href: '/newsletter', title: language === 'hi' ? 'Newsletter' : 'Newsletter', note: language === 'hi' ? 'Email newsletters और campaigns भेजें' : 'Send email newsletters and campaigns', testId: 'school-admin-newsletter-link' },
    ];

    return (
      <div className="space-y-6" data-testid="school-admin-whatsapp-redirect-page">
        <div className="rounded-3xl bg-gradient-to-br from-emerald-500 to-teal-600 p-6 text-white shadow-lg">
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-white/75">
                {language === 'hi' ? 'Communication Update' : 'Communication Update'}
              </p>
              <h1 className="mt-2 text-3xl font-black" data-testid="school-admin-whatsapp-title">
                {language === 'hi' ? 'स्कूल WhatsApp अब Social Media में है' : 'School WhatsApp now lives inside Social Media'}
              </h1>
              <p className="mt-3 max-w-3xl text-sm text-white/90" data-testid="school-admin-whatsapp-message">
                {language === 'hi'
                  ? 'Webhook, API, template approval और platform-level WhatsApp controls अब Platform Admin में रहेंगे। School Admin यहाँ से सिर्फ अपना खुद का WhatsApp Business connect करेगा — Social Media → Platforms → WhatsApp Business.'
                  : 'Webhook, API, template approval, and platform-level WhatsApp controls now stay under Platform Admin. School Admin connects only its own WhatsApp Business from Social Media → Platforms → WhatsApp Business.'}
              </p>
            </div>
            <a
              href="/social-media"
              className="inline-flex items-center justify-center rounded-full bg-white px-5 py-3 text-sm font-bold text-emerald-700 transition hover:-translate-y-0.5 hover:bg-emerald-50"
              data-testid="goto-social-media-platforms-button"
            >
              {language === 'hi' ? 'WhatsApp Connect करें' : 'Connect WhatsApp'}
            </a>
          </div>
        </div>

        <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4 text-sm text-emerald-900" data-testid="school-admin-whatsapp-guidance-card">
          <p className="font-semibold">{language === 'hi' ? 'अब सही communication structure:' : 'Updated communication structure:'}</p>
          <p className="mt-2">
            /communication/Announcements · /communication/Complaints · /communication/Notifications · /communication/Social Media · /communication/Newsletter
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {quickLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-1 hover:shadow-md"
              data-testid={link.testId}
            >
              <h3 className="text-lg font-bold text-slate-900">{link.title}</h3>
              <p className="mt-2 text-sm text-slate-600">{link.note}</p>
              <p className="mt-4 text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700">
                {language === 'hi' ? 'Open module' : 'Open module'}
              </p>
            </a>
          ))}
        </div>
      </div>
    );
  }
  const sendMessage = async () => {
    if (!sendForm.phone || !sendForm.template_name) {
      setToast({ message: 'Phone number aur template select karein', type: 'error' });
      return;
    }
    setSending(true);
    try {
      const params = sendForm.parameters ? sendForm.parameters.split('|').map(p => p.trim()) : [];
      const res = await api.post('/api/whatsapp/send', {
        phone: sendForm.phone,
        template_name: sendForm.template_name,
        language: sendForm.language,
        parameters: params
      }, token);
      
      if (res.success) {
        setToast({ message: `Message sent! ID: ${res.message_id?.substring(0,20)}...`, type: 'success' });
        setSendForm({ phone: '', template_name: '', language: 'en_US', parameters: '' });
        loadLogs();
        loadWebhookStats();
      } else {
        setToast({ message: `Failed: ${res.error}`, type: 'error' });
      }
    } catch (err) {
      setToast({ message: 'Request failed', type: 'error' });
    }
    setSending(false);
  };

  const sendTestHello = async () => {
    if (!sendForm.phone) {
      setToast({ message: 'Phone number dalein', type: 'error' });
      return;
    }
    setSending(true);
    const res = await api.post(`/api/whatsapp/send-test?phone=${sendForm.phone}`, {}, token);
    if (res.success) {
      setToast({ message: 'Hello World sent!', type: 'success' });
      loadLogs();
    } else {
      setToast({ message: `Failed: ${res.error}`, type: 'error' });
    }
    setSending(false);
  };

  const resendMessage = async (log) => {
    setSending(true);
    try {
      const res = await api.post('/api/whatsapp/send', {
        phone: log.phone,
        template_name: log.template,
        language: log.language || 'en_US',
        parameters: log.parameters || []
      }, token);
      if (res.success) {
        setToast({ message: 'Message resent!', type: 'success' });
        loadLogs();
        loadWebhookStats();
      } else {
        setToast({ message: `Resend failed: ${res.error}`, type: 'error' });
      }
    } catch {
      setToast({ message: 'Resend failed', type: 'error' });
    }
    setSending(false);
  };
  
  if (loading) return <div className="p-8 text-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500 mx-auto"></div></div>;

  const deliveryColor = (s) => {
    const colors = { read: 'bg-blue-100 text-blue-700', delivered: 'bg-green-100 text-green-700', sent: 'bg-yellow-100 text-yellow-700', failed: 'bg-red-100 text-red-700' };
    return colors[s] || 'bg-gray-100 text-gray-500';
  };
  
  return (
    <div className="space-y-6" data-testid="whatsapp-dashboard">
      <div className="rounded-3xl bg-gradient-to-br from-emerald-600 via-teal-600 to-slate-900 p-6 text-white shadow-lg" data-testid="platform-whatsapp-hero">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-white/70">{language === 'hi' ? 'Platform Admin Control Center' : 'Platform Admin Control Center'}</p>
            <h2 className="mt-2 text-3xl font-black">{language === 'hi' ? 'WhatsApp Business Control Center' : 'WhatsApp Business Control Center'}</h2>
            <p className="mt-3 max-w-4xl text-sm text-white/85" data-testid="platform-whatsapp-hero-copy">
              {language === 'hi'
                ? 'यहाँ से Platform Admin webhook, delivery tracking, templates, broadcast tools और platform-level WhatsApp governance संभालेगा। School Admin का अपना WhatsApp connection अब Social Media module के अंदर रहेगा।'
                : 'Platform Admin manages webhook, delivery tracking, templates, broadcast tools, and platform-level WhatsApp governance here. School Admin now connects its own WhatsApp inside the Social Media module.'}
            </p>
          </div>
          <span className={`inline-flex items-center rounded-full px-4 py-2 text-sm font-bold ${status?.configured ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`} data-testid="whatsapp-status-badge">
            {status?.configured ? 'LIVE Connected' : 'Not Configured'}
          </span>
        </div>
      </div>
      
      {/* Status Cards */}
      {status?.configured && status?.phone_info && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-white rounded-xl p-4 shadow-sm border-l-4 border-green-500">
            <p className="text-xs text-gray-500">Phone</p>
            <p className="text-sm font-bold text-gray-800">{status.phone_info.display_phone_number}</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border-l-4 border-blue-500">
            <p className="text-xs text-gray-500">Display Name</p>
            <p className="text-sm font-bold text-gray-800">{status.phone_info.verified_name}</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border-l-4 border-purple-500">
            <p className="text-xs text-gray-500">Quality</p>
            <p className="text-sm font-bold text-gray-800">{status.phone_info.quality_rating || 'Pending'}</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border-l-4 border-orange-500">
            <p className="text-xs text-gray-500">Templates</p>
            <p className="text-sm font-bold text-gray-800">{templates.length} Approved</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 flex-wrap">
        {[
          {id: 'control', label: 'Control Center', icon: '🧭'},
          {id: 'inbox', label: `Inbox (Live)${inboxUnread ? ` · ${inboxUnread}` : ''}`, icon: '📥'},
          {id: 'stats', label: 'Delivery Stats', icon: '📊'},
          {id: 'dashboard', label: 'Send Message', icon: '📤'},
          {id: 'templates', label: 'Templates', icon: '📋'},
          {id: 'logs', label: 'Message Logs', icon: '📜'},
          {id: 'webhook', label: 'Webhook', icon: '🔗'},
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`wa-tab-${tab.id}`}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 text-sm ${activeTab === tab.id ? 'bg-green-500 text-white' : 'bg-gray-100 hover:bg-gray-200'}`}>
            <span>{tab.icon}</span> {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'control' && (
        <div className="space-y-6" data-testid="platform-whatsapp-control-center">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            {platformSummaryCards.map((card) => (
              <div key={card.id} className={`rounded-2xl bg-gradient-to-br ${card.color} p-5 text-white shadow-md`} data-testid={`platform-whatsapp-summary-${card.id}`}>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/70">{card.title}</p>
                <p className="mt-3 text-2xl font-black">{card.value}</p>
                <p className="mt-2 text-sm text-white/80">{card.note}</p>
              </div>
            ))}
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="per-school-whatsapp-health-monitor">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">{language === 'hi' ? 'Per-school monitor' : 'Per-school monitor'}</p>
                <h3 className="mt-2 text-2xl font-black text-slate-900">{language === 'hi' ? 'School WhatsApp Health Monitor' : 'School WhatsApp Health Monitor'}</h3>
                <p className="mt-2 text-sm text-slate-500">
                  {language === 'hi'
                    ? 'हर school के WhatsApp connection, token issue, template count और delivery snapshot को यहीं track करें।'
                    : 'Track each school WhatsApp connection, token issue, template count, and delivery snapshot here.'}
                </p>
              </div>

              <div className="flex flex-col gap-3 md:flex-row md:items-center">
                <input
                  value={healthSearch}
                  onChange={(e) => setHealthSearch(e.target.value)}
                  placeholder={language === 'hi' ? 'School नाम या subdomain खोजें' : 'Search school name or subdomain'}
                  className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm focus:border-emerald-500 focus:outline-none"
                  data-testid="school-whatsapp-health-search-input"
                />
                <select
                  value={healthStatusFilter}
                  onChange={(e) => {
                    setHealthStatusFilter(e.target.value);
                    loadSchoolHealth({ status: e.target.value, page: 1 });
                  }}
                  className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm focus:border-emerald-500 focus:outline-none"
                  data-testid="school-whatsapp-health-status-filter"
                >
                  <option value="all">All</option>
                  <option value="connected">Connected</option>
                  <option value="token_issue">Token Issue</option>
                  <option value="not_configured">Not Configured</option>
                  <option value="low_delivery">Low Delivery</option>
                </select>
                <button
                  onClick={() => loadSchoolHealth({ page: 1 })}
                  className="rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700"
                  data-testid="refresh-school-whatsapp-health-button"
                >
                  {healthLoading ? (language === 'hi' ? 'Refresh...' : 'Refresh...') : (language === 'hi' ? 'Refresh Health' : 'Refresh Health')}
                </button>
              </div>
            </div>

            {healthSummary && (
              <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-5" data-testid="school-whatsapp-health-summary-grid">
                {[
                  { id: 'total', label: language === 'hi' ? 'Total Schools' : 'Total Schools', value: healthSummary.total_schools, color: 'bg-slate-50 text-slate-800' },
                  { id: 'connected', label: language === 'hi' ? 'Connected' : 'Connected', value: healthSummary.connected, color: 'bg-green-50 text-green-700' },
                  { id: 'token-issue', label: language === 'hi' ? 'Token Issues' : 'Token Issues', value: healthSummary.token_issue, color: 'bg-red-50 text-red-700' },
                  { id: 'not-configured', label: language === 'hi' ? 'Not Configured' : 'Not Configured', value: healthSummary.not_configured, color: 'bg-gray-100 text-gray-700' },
                  { id: 'low-delivery', label: language === 'hi' ? 'Low Delivery' : 'Low Delivery', value: healthSummary.low_delivery, color: 'bg-orange-50 text-orange-700' },
                ].map((card) => (
                  <div key={card.id} className={`rounded-2xl p-4 ${card.color}`} data-testid={`school-whatsapp-health-summary-${card.id}`}>
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] opacity-70">{card.label}</p>
                    <p className="mt-2 text-2xl font-black">{card.value}</p>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-5 space-y-3" data-testid="school-whatsapp-health-list">
              {healthLoading ? (
                <div className="py-10 text-center text-slate-500">{language === 'hi' ? 'Health data load हो रहा है...' : 'Loading health data...'}</div>
              ) : schoolHealth.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-300 p-8 text-center text-slate-500" data-testid="school-whatsapp-health-empty">
                  {language === 'hi' ? 'कोई school match नहीं मिला।' : 'No school matched your filter.'}
                </div>
              ) : schoolHealth.map((item) => (
                <div key={item.school_id} className="rounded-2xl border border-slate-200 p-4 shadow-sm" data-testid={`school-whatsapp-health-card-${item.school_id}`}>
                  <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <h4 className="text-lg font-bold text-slate-900">{item.school_name}</h4>
                        <span className={`rounded-full px-3 py-1 text-xs font-semibold ${healthBadgeClass(item.health_status)}`}>{item.health_label}</span>
                        {item.delivery?.low_delivery && <span className="rounded-full bg-orange-100 px-3 py-1 text-xs font-semibold text-orange-700">Low Delivery</span>}
                      </div>
                      <p className="mt-1 text-sm text-slate-500">/{item.school_subdomain || 'no-subdomain'} · {item.verified_name || (language === 'hi' ? 'Display name unavailable' : 'Display name unavailable')}</p>
                      <div className="mt-3 grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
                        <div className="rounded-xl bg-slate-50 p-3">
                          <p className="text-xs uppercase tracking-wide text-slate-400">Phone</p>
                          <p className="mt-1 font-semibold text-slate-800">{item.display_phone_number || '—'}</p>
                        </div>
                        <div className="rounded-xl bg-slate-50 p-3">
                          <p className="text-xs uppercase tracking-wide text-slate-400">Templates</p>
                          <p className="mt-1 font-semibold text-slate-800">{item.template_count ?? 0}</p>
                        </div>
                        <div className="rounded-xl bg-slate-50 p-3">
                          <p className="text-xs uppercase tracking-wide text-slate-400">Delivery</p>
                          <p className="mt-1 font-semibold text-slate-800">{item.delivery?.delivery_rate || 0}%</p>
                        </div>
                        <div className="rounded-xl bg-slate-50 p-3">
                          <p className="text-xs uppercase tracking-wide text-slate-400">Token</p>
                          <p className="mt-1 font-semibold text-slate-800">{item.token_days_left == null ? 'Unknown' : `${item.token_days_left}d`}</p>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 xl:justify-end">
                      <button
                        onClick={() => setSelectedHealthSchool(item)}
                        className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                        data-testid={`school-whatsapp-health-details-${item.school_id}`}
                      >
                        {language === 'hi' ? 'Details' : 'Details'}
                      </button>
                      <button
                        onClick={() => loadSchoolHealth({ schoolId: item.school_id })}
                        className="rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
                        data-testid={`school-whatsapp-health-refresh-${item.school_id}`}
                      >
                        {language === 'hi' ? 'Refresh' : 'Refresh'}
                      </button>
                      {item.health_status !== 'connected' && (
                        <button
                          onClick={() => setSelectedHealthSchool(item)}
                          className="rounded-full bg-orange-100 px-4 py-2 text-sm font-semibold text-orange-700 hover:bg-orange-200"
                          data-testid={`school-whatsapp-health-guide-${item.school_id}`}
                        >
                          {language === 'hi' ? 'Setup Guide' : 'Setup Guide'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {healthPagination.total_pages > 1 && (
              <div className="mt-5 flex items-center justify-between" data-testid="school-whatsapp-health-pagination">
                <p className="text-sm text-slate-500">Page {healthPagination.page} / {healthPagination.total_pages}</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => loadSchoolHealth({ page: Math.max(1, healthPagination.page - 1) })}
                    disabled={healthPagination.page <= 1}
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm disabled:opacity-40"
                    data-testid="school-whatsapp-health-prev-page"
                  >Prev</button>
                  <button
                    onClick={() => loadSchoolHealth({ page: Math.min(healthPagination.total_pages, healthPagination.page + 1) })}
                    disabled={healthPagination.page >= healthPagination.total_pages}
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm disabled:opacity-40"
                    data-testid="school-whatsapp-health-next-page"
                  >Next</button>
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-5" data-testid="platform-whatsapp-responsibility-card">
              <h3 className="text-lg font-bold text-emerald-900">{language === 'hi' ? 'Platform Admin की जिम्मेदारियाँ' : 'Platform Admin responsibilities'}</h3>
              <ul className="mt-3 space-y-2 text-sm text-emerald-800">
                <li>• {language === 'hi' ? 'Webhook callback और verify token maintain करना' : 'Maintain webhook callback and verify token'}</li>
                <li>• {language === 'hi' ? 'Template approval / sync / broadcast governance' : 'Template approval / sync / broadcast governance'}</li>
                <li>• {language === 'hi' ? 'Delivery, read rate और logs monitor करना' : 'Monitor delivery, read rate, and logs'}</li>
              </ul>
            </div>

            <div className="rounded-2xl border border-blue-100 bg-blue-50 p-5" data-testid="platform-whatsapp-school-rule-card">
              <h3 className="text-lg font-bold text-blue-900">{language === 'hi' ? 'School-level rule' : 'School-level rule'}</h3>
              <p className="mt-3 text-sm text-blue-800">
                {language === 'hi'
                  ? 'हर school अपना खुद का WhatsApp Business account Social Media → Platforms → WhatsApp Business से connect करेगा। यह page सिर्फ platform-level control के लिए है।'
                  : 'Each school connects its own WhatsApp Business account from Social Media → Platforms → WhatsApp Business. This page is for platform-level control only.'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Delivery Stats Tab */}
      {activeTab === 'stats' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="wa-delivery-stats">
            {[
              { label: 'Total Sent', value: webhookStats?.total_sent || 0, color: 'border-gray-400', bg: 'bg-gray-50' },
              { label: 'Sent', value: webhookStats?.sent || 0, color: 'border-yellow-400', bg: 'bg-yellow-50' },
              { label: 'Delivered', value: webhookStats?.delivered || 0, color: 'border-green-400', bg: 'bg-green-50' },
              { label: 'Read', value: webhookStats?.read || 0, color: 'border-blue-400', bg: 'bg-blue-50' },
              { label: 'Failed', value: webhookStats?.failed || 0, color: 'border-red-400', bg: 'bg-red-50' },
              { label: 'Incoming', value: webhookStats?.incoming || 0, color: 'border-purple-400', bg: 'bg-purple-50' },
            ].map((stat, i) => (
              <div key={i} className={`${stat.bg} rounded-xl p-4 border-l-4 ${stat.color}`}>
                <p className="text-xs text-gray-500">{stat.label}</p>
                <p className="text-2xl font-bold text-gray-800">{stat.value}</p>
              </div>
            ))}
          </div>
          
          {/* Delivery & Read Rate */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h4 className="text-sm font-medium text-gray-500 mb-2">Delivery Rate</h4>
              <div className="flex items-end gap-2">
                <span className="text-4xl font-bold text-green-600">{webhookStats?.delivery_rate || 0}%</span>
                <span className="text-sm text-gray-400 mb-1">delivered + read</span>
              </div>
              <div className="mt-3 w-full bg-gray-200 rounded-full h-3">
                <div className="bg-green-500 h-3 rounded-full transition-all" style={{width: `${Math.min(webhookStats?.delivery_rate || 0, 100)}%`}}></div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h4 className="text-sm font-medium text-gray-500 mb-2">Read Rate</h4>
              <div className="flex items-end gap-2">
                <span className="text-4xl font-bold text-blue-600">{webhookStats?.read_rate || 0}%</span>
                <span className="text-sm text-gray-400 mb-1">messages read</span>
              </div>
              <div className="mt-3 w-full bg-gray-200 rounded-full h-3">
                <div className="bg-blue-500 h-3 rounded-full transition-all" style={{width: `${Math.min(webhookStats?.read_rate || 0, 100)}%`}}></div>
              </div>
            </div>
          </div>
          
          <button onClick={loadWebhookStats} className="text-sm text-green-600 hover:underline" data-testid="wa-refresh-stats">
            Refresh Stats
          </button>
        </div>
      )}
      
      {/* Send Message Tab */}
      {activeTab === 'dashboard' && (
        <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
          <h3 className="font-semibold text-gray-700">Send Template Message</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Phone Number (with country code)</label>
              <input value={sendForm.phone} onChange={(e) => setSendForm({...sendForm, phone: e.target.value})}
                placeholder="919826618603" className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-300 focus:border-green-500" data-testid="wa-phone-input" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Template</label>
              <select value={sendForm.template_name} onChange={(e) => setSendForm({...sendForm, template_name: e.target.value})}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-300" data-testid="wa-template-select">
                <option value="">-- Select Template --</option>
                {templates.map((t, i) => (
                  <option key={i} value={t.name}>{t.name} ({t.category})</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Parameters (pipe | separated)</label>
            <input value={sendForm.parameters} onChange={(e) => setSendForm({...sendForm, parameters: e.target.value})}
              placeholder="Vikas Garg|Demo School|₹2,999" className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-300" data-testid="wa-params-input" />
            <p className="text-xs text-gray-400 mt-1">Example: Param1|Param2|Param3 (order must match template)</p>
          </div>
          <div className="flex gap-3">
            <button onClick={sendMessage} disabled={sending} data-testid="wa-send-btn"
              className="flex-1 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 font-medium">
              {sending ? 'Sending...' : '📤 Send Template Message'}
            </button>
            <button onClick={sendTestHello} disabled={sending} data-testid="wa-test-btn"
              className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 font-medium">
              🧪 Test Hello
            </button>
          </div>
        </div>
      )}

      {/* Templates Tab - Enhanced with Create & Presets */}
      {activeTab === 'templates' && (
        <TemplateManager token={token} templates={templates} loadTemplates={loadTemplates} toast={toast} setToast={setToast} />
      )}

      {/* Logs Tab */}
      {activeTab === 'logs' && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="p-4 border-b flex justify-between items-center">
            <h3 className="font-semibold text-gray-700">Message Logs ({logs.length})</h3>
            <button onClick={loadLogs} className="text-sm text-green-600 hover:underline">Refresh</button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Template</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Delivery</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {logs.map((log, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm">{log.phone}</td>
                    <td className="px-4 py-3 text-sm font-medium">{log.template}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs ${log.status === 'sent' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{log.status}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs ${deliveryColor(log.delivery_status)}`}>
                        {log.delivery_status || 'pending'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">{new Date(log.created_at).toLocaleString('hi-IN')}</td>
                    <td className="px-4 py-3">
                      {log.status === 'failed' || log.delivery_status === 'failed' ? (
                        <button onClick={() => resendMessage(log)} disabled={sending} data-testid={`wa-resend-${idx}`}
                          className="px-2 py-1 text-xs bg-orange-100 text-orange-700 rounded hover:bg-orange-200 disabled:opacity-50">
                          Resend
                        </button>
                      ) : (
                        <span className="text-xs text-gray-300">-</span>
                      )}
                    </td>
                  </tr>
                ))}
                {logs.length === 0 && <tr><td colSpan="6" className="px-4 py-8 text-center text-gray-500">No messages sent yet</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Inbox Tab — inbound messages on the Cloud API number (incl. verification codes) */}
      {activeTab === 'inbox' && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="wa-inbox-panel">
          <div className="p-4 border-b bg-emerald-50">
            <div className="flex flex-wrap justify-between items-center gap-3">
              <div>
                <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                  📥 WhatsApp Inbox <span className="text-xs font-normal text-gray-500">(Live · हर 12s refresh)</span>
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  इस number पर आने वाले सारे messages यहाँ दिखते हैं — verification codes / OTP भी।
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => loadInbox()} className="px-3 py-1.5 text-sm bg-emerald-500 text-white rounded-lg hover:bg-emerald-600" data-testid="wa-inbox-refresh">
                  {inboxLoading ? '...' : '🔄 Refresh'}
                </button>
                {inboxUnread > 0 && (
                  <button onClick={markInboxRead} className="px-3 py-1.5 text-sm border border-emerald-300 text-emerald-700 rounded-lg hover:bg-emerald-100" data-testid="wa-inbox-mark-read">
                    ✓ सभी read ({inboxUnread})
                  </button>
                )}
              </div>
            </div>
            <input
              type="text"
              value={inboxSearch}
              onChange={e => setInboxSearch(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') loadInbox(inboxSearch); }}
              placeholder="🔍 खोजें — number, नाम, message या code... (Enter दबाएँ)"
              className="mt-3 w-full px-3 py-2 border-2 border-emerald-200 rounded-lg text-sm focus:border-emerald-500 outline-none"
              data-testid="wa-inbox-search"
            />
          </div>
          <div className="divide-y max-h-[600px] overflow-y-auto">
            {inbox.map((m, idx) => (
              <div key={m.message_id || idx} className={`p-4 hover:bg-gray-50 ${!m.read ? 'bg-emerald-50/40' : ''}`} data-testid={`wa-inbox-row-${idx}`}>
                <div className="flex justify-between items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-800 text-sm">{m.name || 'WhatsApp User'}</span>
                      <span className="text-xs text-gray-400">{m.phone}</span>
                      {!m.read && <span className="text-[10px] bg-emerald-500 text-white px-1.5 py-0.5 rounded-full">NEW</span>}
                    </div>
                    <p className="mt-1 text-sm text-gray-700 break-words whitespace-pre-wrap">{m.body}</p>
                    {m.code && (
                      <div className="mt-2 inline-flex items-center gap-2 bg-amber-100 border border-amber-300 rounded-lg px-3 py-1.5" data-testid={`wa-inbox-code-${idx}`}>
                        <span className="text-xs text-amber-700">🔑 Code:</span>
                        <span className="font-mono font-bold text-amber-900 tracking-widest select-all">{m.code}</span>
                        <button
                          onClick={() => { navigator.clipboard?.writeText(m.code); setToast({ type: 'success', message: `Code ${m.code} copy हो गया` }); }}
                          className="text-xs text-amber-700 underline hover:text-amber-900">copy</button>
                      </div>
                    )}
                  </div>
                  <span className="text-[11px] text-gray-400 whitespace-nowrap">{m.created_at ? new Date(m.created_at).toLocaleString('hi-IN') : ''}</span>
                </div>
              </div>
            ))}
            {inbox.length === 0 && (
              <div className="px-4 py-12 text-center text-gray-500 text-sm" data-testid="wa-inbox-empty">
                अभी कोई inbound message नहीं आया।<br />
                <span className="text-xs text-gray-400">Meta पर "Resend code" दबाएँ — कुछ सेकंड में code यहाँ दिख जाएगा।</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Webhook Tab */}
      {activeTab === 'webhook' && (
        <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
          <h3 className="font-semibold text-gray-700">Webhook Configuration</h3>
          <p className="text-sm text-gray-500">Meta Developer Portal mein ye settings configure karein:</p>
          <div className="space-y-3">
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-xs font-medium text-gray-500 mb-1">Callback URL</p>
              <code className="text-sm text-green-700 bg-green-50 px-3 py-1 rounded block" data-testid="webhook-url">
                {window.location.origin}/api/whatsapp/webhook
              </code>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-xs font-medium text-gray-500 mb-1">Verify Token</p>
              <code className="text-sm text-green-700 bg-green-50 px-3 py-1 rounded block">
                shiksha_mitra_wh_verify_2026
              </code>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-xs font-medium text-gray-500 mb-1">Webhook Fields (Subscribe karein)</p>
              <div className="flex flex-wrap gap-2 mt-1">
                {['messages', 'message_template_status_update'].map(f => (
                  <span key={f} className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">{f}</span>
                ))}
              </div>
            </div>
          </div>
          <div className="bg-blue-50 rounded-lg p-4 text-sm text-blue-700">
            <p className="font-medium mb-1">Setup Steps:</p>
            <ol className="list-decimal ml-4 space-y-1">
              <li>developers.facebook.com par jayen → Shiksha Mitra app</li>
              <li>WhatsApp → Configuration page</li>
              <li>Webhook section mein "Edit" click karein</li>
              <li>Callback URL aur Verify Token paste karein</li>
              <li>"Verify and Save" click karein</li>
              <li>Webhook fields mein "messages" subscribe karein</li>
            </ol>
          </div>
        </div>
      )}

      {selectedHealthSchool && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="school-whatsapp-health-details-modal">
          <div className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b p-5">
              <div>
                <h3 className="text-xl font-bold text-slate-900">{selectedHealthSchool.school_name}</h3>
                <p className="text-sm text-slate-500">/{selectedHealthSchool.school_subdomain || 'no-subdomain'}</p>
              </div>
              <button onClick={() => setSelectedHealthSchool(null)} className="text-2xl text-slate-400 hover:text-slate-700" data-testid="close-school-whatsapp-health-modal">×</button>
            </div>
            <div className="space-y-5 p-5">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div className="rounded-xl bg-slate-50 p-4">
                  <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Status</p>
                  <p className="mt-2 font-semibold text-slate-900">{selectedHealthSchool.health_label}</p>
                </div>
                <div className="rounded-xl bg-slate-50 p-4">
                  <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Last Checked</p>
                  <p className="mt-2 font-semibold text-slate-900">{selectedHealthSchool.last_checked_at ? new Date(selectedHealthSchool.last_checked_at).toLocaleString('hi-IN') : '—'}</p>
                </div>
                <div className="rounded-xl bg-slate-50 p-4">
                  <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Display Name</p>
                  <p className="mt-2 font-semibold text-slate-900">{selectedHealthSchool.verified_name || '—'}</p>
                </div>
                <div className="rounded-xl bg-slate-50 p-4">
                  <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Phone</p>
                  <p className="mt-2 font-semibold text-slate-900">{selectedHealthSchool.display_phone_number || '—'}</p>
                </div>
                <div className="rounded-xl bg-slate-50 p-4">
                  <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Templates</p>
                  <p className="mt-2 font-semibold text-slate-900">{selectedHealthSchool.template_count ?? 0}</p>
                </div>
                <div className="rounded-xl bg-slate-50 p-4">
                  <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Token Days Left</p>
                  <p className="mt-2 font-semibold text-slate-900">{selectedHealthSchool.token_days_left == null ? 'Unknown' : `${selectedHealthSchool.token_days_left} days`}</p>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 p-4">
                <h4 className="font-bold text-slate-900">{language === 'hi' ? 'Delivery Snapshot (30d)' : 'Delivery Snapshot (30d)'}</h4>
                <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4">
                  <div className="rounded-xl bg-green-50 p-3 text-center"><p className="text-xs text-green-700">Delivery</p><p className="mt-1 font-bold text-green-800">{selectedHealthSchool.delivery?.delivery_rate || 0}%</p></div>
                  <div className="rounded-xl bg-blue-50 p-3 text-center"><p className="text-xs text-blue-700">Read</p><p className="mt-1 font-bold text-blue-800">{selectedHealthSchool.delivery?.read_rate || 0}%</p></div>
                  <div className="rounded-xl bg-orange-50 p-3 text-center"><p className="text-xs text-orange-700">Sent</p><p className="mt-1 font-bold text-orange-800">{selectedHealthSchool.delivery?.total_sent || 0}</p></div>
                  <div className="rounded-xl bg-red-50 p-3 text-center"><p className="text-xs text-red-700">Failed</p><p className="mt-1 font-bold text-red-800">{selectedHealthSchool.delivery?.failed || 0}</p></div>
                </div>
              </div>

              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="school-whatsapp-health-setup-guide-box">
                <h4 className="font-bold text-amber-900">{language === 'hi' ? 'Recommended Setup Guide' : 'Recommended Setup Guide'}</h4>
                <ol className="mt-3 list-decimal space-y-1 pl-5 text-sm text-amber-800">
                  <li>{language === 'hi' ? 'School Admin से Social Media → Platforms → WhatsApp Business open करवाएँ' : 'Ask School Admin to open Social Media → Platforms → WhatsApp Business'}</li>
                  <li>{language === 'hi' ? 'Phone Number ID, Business Account ID, Access Token save करवाएँ' : 'Save Phone Number ID, Business Account ID, and Access Token'}</li>
                  <li>{language === 'hi' ? 'Connected होने के बाद यहाँ Refresh Health दबाएँ' : 'After connection, click Refresh Health here'}</li>
                </ol>
                {selectedHealthSchool.issue && (
                  <p className="mt-3 text-sm font-medium text-red-700">Issue: {selectedHealthSchool.issue}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

// ==================== REPORTS PAGE ====================
export function ReportsPage({ token }) {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [filters, setFilters] = useState({ class_id: '', section_id: '', date: new Date().toISOString().split('T')[0] });
  
  useEffect(() => { loadClasses(); }, []);
  
  const loadClasses = async () => {
    try {
      const res = await api.get('/api/classes/', token);
      setClasses(res.classes || []);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };
  
  const generateReport = async (type) => {
    setGenerating(true);
    try {
      const params = new URLSearchParams(filters).toString();
      const res = await fetch(`${API_URL}/api/reports/${type}?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${type}_report_${filters.date}.xlsx`;
        a.click();
      }
    } catch (err) {
      console.error(err);
    }
    setGenerating(false);
  };
  
  const sections = classes.find(c => c.id === filters.class_id)?.sections || [];
  
  if (loading) return <div className="p-8 text-center">Loading...</div>;
  
  const reports = [
    { type: 'attendance', name: 'Attendance Report', icon: '✅', color: 'green' },
    { type: 'fee-collection', name: 'Fee Collection Report', icon: '💰', color: 'blue' },
    { type: 'student-list', name: 'Student List', icon: '👨‍🎓', color: 'purple' },
    { type: 'teacher-list', name: 'Teacher List', icon: '👨‍🏫', color: 'orange' },
  ];
  
  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-800">Reports</h2>
      
      <div className="bg-white rounded-xl shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <select value={filters.class_id} onChange={(e) => setFilters({...filters, class_id: e.target.value, section_id: ''})} className="px-4 py-2 border rounded-lg">
            <option value="">All Classes</option>
            {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select value={filters.section_id} onChange={(e) => setFilters({...filters, section_id: e.target.value})} className="px-4 py-2 border rounded-lg" disabled={!filters.class_id}>
            <option value="">All Sections</option>
            {sections.map(s => <option key={s.id} value={s.id}>Section {s.name}</option>)}
          </select>
          <input type="date" value={filters.date} onChange={(e) => setFilters({...filters, date: e.target.value})} className="px-4 py-2 border rounded-lg" />
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {reports.map(report => (
          <div key={report.type} className="bg-white rounded-xl shadow p-6">
            <div className="text-center mb-4">
              <span className="text-4xl">{report.icon}</span>
              <h3 className="font-semibold mt-2">{report.name}</h3>
            </div>
            <button 
              onClick={() => generateReport(report.type)} 
              disabled={generating}
              className={`w-full py-2 rounded-lg text-white bg-${report.color}-500 hover:bg-${report.color}-600 disabled:opacity-50`}
            >
              {generating ? 'Generating...' : 'Download'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ==================== LEAVE MANAGEMENT PAGE ====================
export function LeaveManagementPage({ token, user }) {
  const { language } = useLanguage();
  const [pendingLeaves, setPendingLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeLevel, setActiveLevel] = useState('teacher');
  
  useEffect(() => { loadPendingLeaves(); }, [activeLevel]);
  
  const loadPendingLeaves = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/leave/pending-approvals?approval_level=${activeLevel}`, token);
      setPendingLeaves(res.requests || []);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };
  
  const handleApproval = async (leaveId, action) => {
    try {
      await api.post('/api/leave/approve', { leave_id: leaveId, action }, token);
      loadPendingLeaves();
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };
  
  return (
    <div className="space-y-6" data-testid="leave-management-page">
      <h1 className="text-2xl font-bold text-gray-800">📝 {language === 'hi' ? 'छुट्टी प्रबंधन' : 'Leave Management'}</h1>
      
      <div className="flex gap-2">
        {['teacher', 'incharge', 'admin'].map(level => (
          <button key={level} onClick={() => setActiveLevel(level)}
            className={`px-4 py-2 rounded-lg transition ${activeLevel === level ? 'bg-orange-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-100'}`}>
            {level.charAt(0).toUpperCase() + level.slice(1)} Approval
          </button>
        ))}
      </div>
      
      {loading ? (
        <div className="flex justify-center py-8"><div className="animate-spin h-8 w-8 border-4 border-orange-500 border-t-transparent rounded-full"></div></div>
      ) : pendingLeaves.length === 0 ? (
        <div className="bg-white rounded-xl p-8 text-center">
          <span className="text-4xl block mb-2">✅</span>
          <p className="text-gray-500">{language === 'hi' ? 'कोई pending छुट्टी नहीं है' : 'No pending leaves'}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {pendingLeaves.map(leave => (
            <div key={leave.leave_id} className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="font-medium">{leave.student_name}</h3>
                  <p className="text-sm text-gray-500">{leave.student_class} | {leave.from_date} to {leave.to_date} ({leave.num_days} days)</p>
                </div>
                <span className={`px-2 py-1 rounded text-xs ${leave.leave_type === 'sick' ? 'bg-red-100 text-red-700' : leave.leave_type === 'family' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100'}`}>
                  {leave.leave_type}
                </span>
              </div>
              <p className="text-sm text-gray-600 mb-3">{leave.reason}</p>
              <div className="flex gap-2">
                <button onClick={() => handleApproval(leave.leave_id, 'approve')} className="px-4 py-2 bg-green-500 text-white rounded-lg text-sm hover:bg-green-600">✓ Approve</button>
                <button onClick={() => handleApproval(leave.leave_id, 'reject')} className="px-4 py-2 bg-red-500 text-white rounded-lg text-sm hover:bg-red-600">✗ Reject</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ==================== LEAVE APPLY PAGE ====================
export function LeaveApplyPage({ token, user }) {
  const { language } = useLanguage();
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [formData, setFormData] = useState({ student_id: '', from_date: '', to_date: '', reason: '' });
  const [myLeaves, setMyLeaves] = useState([]);
  const [loading, setLoading] = useState(false);
  
  useEffect(() => { loadTemplates(); loadMyLeaves(); }, []);
  
  const loadTemplates = async () => {
    try {
      const res = await api.get('/api/leave/templates?language=hindi', token);
      setTemplates(res.templates || []);
    } catch (err) {
      console.error(err);
    }
  };
  
  const loadMyLeaves = async () => {
    try {
      const res = await api.get('/api/leave/my-requests?limit=10', token);
      setMyLeaves(res.requests || []);
    } catch (err) {
      console.error(err);
    }
  };
  
  const handleSubmit = async () => {
    if (!formData.student_id || !formData.from_date || !formData.to_date) {
      alert('Please fill all required fields');
      return;
    }
    setLoading(true);
    try {
      const payload = { ...formData, template_id: selectedTemplate?.id, leave_type: selectedTemplate?.type || 'other' };
      await api.post('/api/leave/request', payload, token);
      alert('छुट्टी का आवेदन जमा हो गया!');
      setSelectedTemplate(null);
      setFormData({ student_id: '', from_date: '', to_date: '', reason: '' });
      loadMyLeaves();
    } catch (err) {
      alert('Error: ' + err.message);
    }
    setLoading(false);
  };
  
  return (
    <div className="space-y-6" data-testid="leave-apply-page">
      <h1 className="text-2xl font-bold text-gray-800">📝 {language === 'hi' ? 'छुट्टी के लिए आवेदन' : 'Apply for Leave'}</h1>
      
      {!selectedTemplate ? (
        <>
          <p className="text-gray-500">{language === 'hi' ? 'छुट्टी का कारण चुनें:' : 'Select leave reason:'}</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {templates.map(t => (
              <button key={t.id} onClick={() => { setSelectedTemplate(t); setFormData({...formData, reason: t.default_reason}); }}
                className="p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition text-center" style={{borderLeft: `4px solid ${t.color}`}}>
                <span className="text-3xl block mb-2">{t.icon}</span>
                <span className="font-medium">{t.name}</span>
                <p className="text-xs text-gray-500 mt-1">{t.max_days} {language === 'hi' ? 'दिन max' : 'days max'}</p>
              </button>
            ))}
          </div>
        </>
      ) : (
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-3xl">{selectedTemplate.icon}</span>
            <div>
              <h3 className="font-bold">{selectedTemplate.name}</h3>
              <p className="text-sm text-gray-500">{selectedTemplate.description}</p>
            </div>
            <button onClick={() => setSelectedTemplate(null)} className="ml-auto text-gray-400 hover:text-gray-600">✕</button>
          </div>
          
          <div className="space-y-4">
            <input placeholder="Student ID" value={formData.student_id} onChange={e => setFormData({...formData, student_id: e.target.value})} className="w-full px-4 py-3 border rounded-lg" />
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-500 block mb-1">{language === 'hi' ? 'से' : 'From'}</label>
                <input type="date" value={formData.from_date} onChange={e => setFormData({...formData, from_date: e.target.value})} className="w-full px-4 py-3 border rounded-lg" />
              </div>
              <div>
                <label className="text-sm text-gray-500 block mb-1">{language === 'hi' ? 'तक' : 'To'}</label>
                <input type="date" value={formData.to_date} onChange={e => setFormData({...formData, to_date: e.target.value})} className="w-full px-4 py-3 border rounded-lg" />
              </div>
            </div>
            <textarea placeholder={language === 'hi' ? 'कारण विस्तार से...' : 'Reason details...'} value={formData.reason} onChange={e => setFormData({...formData, reason: e.target.value})} className="w-full px-4 py-3 border rounded-lg h-24" />
            <button onClick={handleSubmit} disabled={loading} className="w-full py-3 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 disabled:opacity-50">
              {loading ? 'Submitting...' : (language === 'hi' ? 'आवेदन जमा करें' : 'Submit Application')}
            </button>
          </div>
        </div>
      )}
      
      {myLeaves.length > 0 && (
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h3 className="font-bold mb-4">{language === 'hi' ? 'मेरे आवेदन' : 'My Applications'}</h3>
          <div className="space-y-3">
            {myLeaves.map(leave => (
              <div key={leave.leave_id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <div>
                  <span className="font-medium">{leave.from_date} - {leave.to_date}</span>
                  <p className="text-xs text-gray-500">{leave.leave_type}</p>
                </div>
                <span className={`px-2 py-1 rounded text-xs ${leave.status.includes('approved') ? 'bg-green-100 text-green-700' : leave.status.includes('rejected') ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
                  {leave.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== EMERGENCY EXIT PAGE ====================
export function EmergencyExitPage({ token, user }) {
  const { language } = useLanguage();
  const [dashboard, setDashboard] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  
  useEffect(() => { loadDashboard(); }, []);
  
  const loadDashboard = async () => {
    try {
      const res = await api.get('/api/emergency-exit/dashboard', token);
      setDashboard(res);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };
  
  const handleCreateRequest = async () => {
    try {
      await api.post('/api/emergency-exit/request', formData, token);
      alert('Emergency exit request created!');
      setShowCreateForm(false);
      setFormData({});
      loadDashboard();
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };
  
  if (loading) return <div className="p-8 text-center">Loading...</div>;
  
  return (
    <div className="space-y-6" data-testid="emergency-exit-page">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-800">🚨 {language === 'hi' ? 'आपातकालीन बाहर जाना' : 'Emergency Exit'}</h1>
        <button onClick={() => setShowCreateForm(true)} className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600">
          + {language === 'hi' ? 'नया अनुरोध' : 'New Request'}
        </button>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-yellow-50 rounded-xl p-4">
          <h3 className="font-medium text-yellow-700">{language === 'hi' ? 'लंबित' : 'Pending'}</h3>
          <p className="text-3xl font-bold text-yellow-600">{dashboard?.pending_count || 0}</p>
        </div>
        <div className="bg-green-50 rounded-xl p-4">
          <h3 className="font-medium text-green-700">{language === 'hi' ? 'स्वीकृत' : 'Approved'}</h3>
          <p className="text-3xl font-bold text-green-600">{dashboard?.approved_today || 0}</p>
        </div>
        <div className="bg-blue-50 rounded-xl p-4">
          <h3 className="font-medium text-blue-700">{language === 'hi' ? 'आज कुल' : 'Today Total'}</h3>
          <p className="text-3xl font-bold text-blue-600">{dashboard?.total_today || 0}</p>
        </div>
      </div>
      
      {dashboard?.recent_requests?.length > 0 && (
        <div className="bg-white rounded-xl shadow">
          <div className="p-4 border-b">
            <h3 className="font-semibold">{language === 'hi' ? 'हाल के अनुरोध' : 'Recent Requests'}</h3>
          </div>
          <div className="divide-y">
            {dashboard.recent_requests.map(req => (
              <div key={req.id} className="p-4 flex justify-between items-center">
                <div>
                  <h4 className="font-medium">{req.student_name}</h4>
                  <p className="text-sm text-gray-500">{req.reason}</p>
                </div>
                <span className={`px-2 py-1 rounded text-xs ${req.status === 'approved' ? 'bg-green-100 text-green-700' : req.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
                  {req.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {showCreateForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-bold mb-4">{language === 'hi' ? 'आपातकालीन बाहर जाने का अनुरोध' : 'Emergency Exit Request'}</h3>
            <div className="space-y-4">
              <input placeholder="Student ID" value={formData.student_id || ''} onChange={e => setFormData({...formData, student_id: e.target.value})} className="w-full px-4 py-2 border rounded-lg" />
              <select value={formData.reason_type || ''} onChange={e => setFormData({...formData, reason_type: e.target.value})} className="w-full px-4 py-2 border rounded-lg">
                <option value="">Select Reason</option>
                <option value="medical">Medical Emergency</option>
                <option value="family">Family Emergency</option>
                <option value="other">Other</option>
              </select>
              <textarea placeholder="Details..." value={formData.reason || ''} onChange={e => setFormData({...formData, reason: e.target.value})} className="w-full px-4 py-2 border rounded-lg h-24" />
              <div className="flex gap-2">
                <button onClick={() => setShowCreateForm(false)} className="flex-1 py-2 border rounded-lg">Cancel</button>
                <button onClick={handleCreateRequest} className="flex-1 py-2 bg-red-500 text-white rounded-lg">Submit</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default {
  WhatsAppPage,
  ReportsPage,
  LeaveManagementPage,
  LeaveApplyPage,
  EmergencyExitPage
};
